export interface ICookieConsentChoice {
  analytics: boolean;
  marketing: boolean;
  decidedAt: number;
}

export interface ICookieConsentParseResult {
  choice: ICookieConsentChoice | null;
  isLegacy: boolean;
}

type TConsentSignal = 'granted' | 'denied';

export interface IGoogleConsentState {
  analytics_storage: TConsentSignal;
  ad_storage: TConsentSignal;
  ad_user_data: TConsentSignal;
  ad_personalization: TConsentSignal;
}

export const COOKIE_CONSENT_NAME = 'cookieConsent';
export const COOKIE_CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const COOKIE_CONSENT_PATTERN = /^2\.([01])\.([01])\.(\d{9,11})$/;

export const nowInSeconds = (): number => Math.floor(Date.now() / 1000);

export const parseCookieConsent = (
  raw: string | null | undefined,
  now: number,
): ICookieConsentParseResult => {
  if (raw === 'granted' || raw === 'denied') {
    const isGranted = raw === 'granted';

    return {
      choice: { analytics: isGranted, marketing: isGranted, decidedAt: now },
      isLegacy: true,
    };
  }

  const match = raw ? COOKIE_CONSENT_PATTERN.exec(raw) : null;

  if (!match) {
    return { choice: null, isLegacy: false };
  }

  const decidedAt = Number(match[3]);

  if (decidedAt <= now - COOKIE_CONSENT_MAX_AGE_SECONDS) {
    return { choice: null, isLegacy: false };
  }

  return {
    choice: {
      analytics: match[1] === '1',
      marketing: match[2] === '1',
      decidedAt,
    },
    isLegacy: false,
  };
};

export const formatCookieConsent = (choice: ICookieConsentChoice): string =>
  `2.${choice.analytics ? 1 : 0}.${choice.marketing ? 1 : 0}.${choice.decidedAt}`;

export const hasAnyConsent = (
  choice: ICookieConsentChoice | null,
): choice is ICookieConsentChoice =>
  choice !== null && (choice.analytics || choice.marketing);

export const isConsentReduced = (
  previous: ICookieConsentChoice | null,
  next: ICookieConsentChoice,
): boolean =>
  previous !== null &&
  ((previous.analytics && !next.analytics) ||
    (previous.marketing && !next.marketing));

export const buildGoogleConsentState = (
  choice: ICookieConsentChoice,
): IGoogleConsentState => {
  const marketing: TConsentSignal = choice.marketing ? 'granted' : 'denied';

  return {
    analytics_storage: choice.analytics ? 'granted' : 'denied',
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  };
};
